import {
  deleteEntity,
  deleteEntityFailure,
  deleteEntityFulfill,
  deleteEntityRequest,
  deleteEntitySuccess,
  deletePrompt,
  deletePromptFailure,
  deletePromptRequest,
  deletePromptSuccess,
  fetchEntities,
  fetchEntitiesFailure,
  fetchEntitiesFulfill,
  fetchEntitiesRequest,
  fetchEntitiesSuccess,
  fetchPrompts,
  fetchPromptsFailure,
  fetchPromptsFulfill,
  fetchPromptsRequest,
  fetchPromptsSuccess,
  fetchSinglePrompt,
  fetchSinglePromptFailure,
  fetchSinglePromptRequest,
  fetchSinglePromptSuccess,
  mergeEntities,
  mergeEntitiesFailure,
  mergeEntitiesRequest,
  mergeEntitiesSuccess,
  pushPathname,
  showActionAlert,
  showSuccessAlert,
  upsertPrompt,
  upsertPromptFailure,
  upsertPromptRequest,
  upsertPromptSuccess,
} from 'actions';
import { apiClient as LitLingoClient } from 'client';
import { push } from 'connected-react-router';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getCustomerDomain } from 'selectors/auth';
import { getNavParamsByResource } from 'selectors/nav';
import { API, SagaReturn } from 'types';
import { reverse } from 'utils/urls';

function* fetchSinglePromptsaga(action: ReturnType<typeof fetchSinglePrompt>): SagaReturn {
  const { payload } = action;
  yield put(fetchSinglePromptRequest());
  const response = (yield call([LitLingoClient.resources.prompts, 'retrieve'], payload.promptId, {
    params: {
      relationships: ['schema_json', 'in_use'],
    },
  })) as API.Response<API.Prompts.Retrieve>;
  if (response.error != null) {
    yield put(fetchSinglePromptFailure(response.error));
  } else {
    yield put(fetchSinglePromptSuccess(response.data));
  }
}

function* fetchEntitiesSaga(): SagaReturn {
  yield put(fetchEntitiesRequest());

  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.entities)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;

  const response = (yield call([LitLingoClient.resources.entities, 'list'], {
    params: {
      relationships: ['entity_impressions', 'entity_impressions.divisions'],
      ...resourceParams,
      include_count: true,
    },
  })) as API.Response<API.Entities.List>;

  if (response.error != null) {
    yield put(fetchEntitiesFailure(response.error));
  } else {
    yield put(fetchEntitiesSuccess(response.data));
  }

  yield put(fetchEntitiesFulfill());
}

function* deleteEntitySaga({ payload }: ReturnType<typeof deleteEntity>): SagaReturn {
  yield put(deleteEntityRequest());

  const response = (yield call([LitLingoClient.resources.entities, 'delete'], payload, {
    params: {
      relationships: ['entity_impressions', 'entity_impressions.divisions'],
    },
  })) as API.Response<API.Entities.Delete>;

  if (response.error != null) {
    yield put(deleteEntityFailure(response.error));
  } else {
    yield put(deleteEntitySuccess(payload));
  }

  yield put(deleteEntityFulfill());
}

function* mergeEntitiesSaga({ payload }: ReturnType<typeof mergeEntities>): SagaReturn {
  const { source, destination } = payload;

  yield put(mergeEntitiesRequest());

  const response = (yield call([LitLingoClient.resources.entities.extras, 'merge'], {
    data: {
      entity_uuid: source,
      destination_entity_uuid: destination,
    },
  })) as API.Response<API.Entities.Merge>;

  if (response.error != null) {
    yield put(mergeEntitiesFailure(response.error));
  } else {
    yield put(mergeEntitiesSuccess(source));
  }
}

function* fetchPromptsSaga(action: ReturnType<typeof fetchPrompts>): SagaReturn {
  const payloadParams = action?.payload || {};

  yield put(fetchPromptsRequest());

  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.prompts)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;

  const params: { include_count: boolean; kind?: string; relationships: string[] } = {
    include_count: true,
    relationships: ['schema_json', 'in_use'],
    ...resourceParams,
    ...payloadParams,
  };

  const response = (yield call([LitLingoClient.resources.prompts, 'list'], {
    params,
  })) as API.Response<API.Prompts.List>;

  if (response.error != null) {
    yield put(fetchPromptsFailure(response.error));
  } else {
    yield put(fetchPromptsSuccess(response.data));
  }

  yield put(fetchPromptsFulfill());
}

function* upsertPromptSaga(action: ReturnType<typeof upsertPrompt>): SagaReturn {
  const { payload } = action;
  const { relationships, isEditing = false, isCopying = false, ...rest } = payload;
  const params = {
    relationships: relationships || ['schema_json'],
  };
  const customerDomain =
    ((yield select(getCustomerDomain)) as ReturnType<typeof getCustomerDomain>) || '';

  yield put(upsertPromptRequest());

  const response = (yield call([LitLingoClient.resources.prompts, 'upsert'], {
    params,
    data: rest,
  })) as API.Response<API.Prompts.Upsert>;

  if (response.error != null) {
    yield put(upsertPromptFailure(response.error));
    if (isEditing) {
      yield put(
        showSuccessAlert("Something went wrong and the prompt can't be saved at this time")
      );
    } else {
      yield put(
        showSuccessAlert("Something went wrong and the new prompt can't be saved at this time")
      );
    }
  } else {
    yield put(upsertPromptSuccess(response.data));

    if (isEditing) {
      yield put(showSuccessAlert(`${rest.name} has been saved successfully`));
    } else if (isCopying) {
      const pathname = reverse({
        routeName: 'prompt-detail',
        customerDomain,
        routeParams: { promptId: response.data.uuid },
      });

      yield put(
        showActionAlert({
          action: pushPathname,
          actionMsg: `View ${rest.name}`,
          actionPayload: {
            pathname,
          },
          id: 'id',
          msg: `${rest.name} has been created successfully`,
          timeout: 3000,
        })
      );
    } else {
      yield put(showSuccessAlert(`${rest.name} has been created successfully`));
    }
    if (payload.redirect) {
      const path = reverse({
        routeName: 'prompt-detail',
        customerDomain,
        routeParams: { promptId: response.data.uuid },
      });
      yield put(push(path));
    }
  }
}

function* deletePromptSaga(action: ReturnType<typeof deletePrompt>): SagaReturn {
  const { uuid, redirect } = action.payload;

  yield put(deletePromptRequest());

  const response = (yield call(
    [LitLingoClient.resources.prompts, 'delete'],
    uuid
  )) as API.Response<API.Prompts.Upsert>;

  if (response.error != null) {
    yield put(deletePromptFailure(response.error));
  } else {
    yield put(deletePromptSuccess({ uuid }));
    yield put(showSuccessAlert('Prompt deleted'));
    if (redirect) {
      const customerDomain =
        ((yield select(getCustomerDomain)) as ReturnType<typeof getCustomerDomain>) || '';

      const path = reverse({
        routeName: 'prompts',
        customerDomain,
      });
      yield put(push(path));
    }
  }
}

function* entitiesSaga(): SagaReturn {
  yield takeLatest(fetchSinglePrompt.toString(), fetchSinglePromptsaga);
  yield takeLatest(fetchEntities.toString(), fetchEntitiesSaga);
  yield takeEvery(deleteEntity.toString(), deleteEntitySaga);
  yield takeLatest(mergeEntities.toString(), mergeEntitiesSaga);
  yield takeLatest(fetchPrompts.toString(), fetchPromptsSaga);
  yield takeLatest(upsertPrompt.toString(), upsertPromptSaga);
  yield takeLatest(deletePrompt.toString(), deletePromptSaga);
}

export default entitiesSaga;
